<template>
  <Modal ref="modalRef" @ok="onCreateNew" @hidden="onHidden">
    <template #title>Already uploaded?</template>

    <p>You already have a tree with the same file name.</p>

    <div v-if="firstMatchRef" class="match">
      <WorkspaceSourcePanel :workspace-id="firstMatchRef.workspaceId"/>
      <button type="button" class="overlay" aria-label="View existing tree" @click="onUpdateExisting"></button>
    </div>

    <p>Do you want to continue?</p>

    <template #okText>Create a new tree</template>
  </Modal>
</template>

<style lang="scss" scoped>
.match {
  position: relative;
  margin: 1rem;
  padding: 0.5rem;
  box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.15);
  cursor: pointer;

  &:hover {
    background-color: #f8f8f8;
  }

  .overlay {
    position: absolute;
    inset: 0;
    border: 0;
    background: transparent;
  }
}
</style>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import Modal from '@webapp/util/Modal.vue'
import WorkspaceSourcePanel from './WorkspaceSourcePanel.vue'
import { WorkspaceMatch } from './WorkspaceFinder.js'

const props = defineProps<{
  matches: WorkspaceMatch[]
}>()

const emit = defineEmits<{
  (e: 'update', workspaceId: string): void
  (e: 'createNew'): void
  (e: 'cancel'): void
}>()

const modalRef = ref<InstanceType<typeof Modal>>()
let decided = false

// Only the first match is shown -- same-name duplicates are rare, and one clear "you already have
// this" reads better than a list.
const firstMatchRef = computed(() => props.matches[0])

async function open() {
  decided = false
  // Wait for props to reflect the matches the caller just assigned before showing them.
  await nextTick()
  modalRef.value?.open()
}

function close() {
  modalRef.value?.close()
}

defineExpose({ open, close })

function onCreateNew() {
  decided = true
  emit('createNew')
  modalRef.value?.close()
}

async function onUpdateExisting() {
  if (!firstMatchRef.value) {
    return
  }
  decided = true
  // Fully hide the modal (and its backdrop) before the parent navigates away.
  await modalRef.value?.close()
  emit('update', firstMatchRef.value.workspaceId)
}

function onHidden() {
  // A dismiss with no choice made is a real cancel -- back out, don't create or update.
  if (!decided) {
    emit('cancel')
  }
}
</script>
